import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {BsdfTranslucentSockets} from "#material/nodes/shader/bsdf-translucent/definition"
import {bsdfTranslucentDefinition} from "#material/nodes/shader/bsdf-translucent/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {threeConvert, threeRGBColor, threeVec3} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {cyclesColor, cyclesVec3} from "#material/cycles/helpers"
import type {ThreeReturns} from "#material/sockets/three"
import type {TranslucentBsdfNode} from "#material/cycles/types"
import {ThreeColormassMaterial} from "#material/three/three-colormass-material"

@registerMaterialGraphNode
export class BsdfTranslucent extends MaterialNode<BsdfTranslucentSockets> {
    public override get definition() {
        return bsdfTranslucentDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<BsdfTranslucentSockets>> => {
        const material = new ThreeColormassMaterial()

        const {color, normal} = await evaluator.getAll({
            color: this.parameters.inputs.color,
            normal: this.parameters.inputs.normal,
        })

        material.normalNode =
            normal?.toVec3() ??
            threeConvert(this.parameters.settings.normal, threeVec3, (val) => val.x !== 0 || val.y !== 0 || val.z !== 0) ??
            material.normalNode

        const transmissionValue =
            color?.toColor() ??
            threeConvert(this.parameters.settings.color, threeRGBColor, (val) => val.r > 0.05 || val.g > 0.05 || val.b > 0.05) ??
            material.transmissionNode

        if (transmissionValue) {
            material.transparent = true
            material.transmissionNode = transmissionValue
            material.transmission = 0.01
        }

        return {bsdf: material}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<BsdfTranslucentSockets>> => {
        const bsdf: TranslucentBsdfNode = {
            type: "translucent_bsdf",
            inputs: await evaluator.getAll({
                color: this.parameters.inputs.color,
                normal: this.parameters.inputs.normal,
                surface_mix_weight: undefined,
            }),
            parameters: {
                color: cyclesColor(this.parameters.settings.color),
                normal: cyclesVec3(this.parameters.settings.normal),
                surface_mix_weight: undefined,
            },
            resources: {},
        }

        return {bsdf: [bsdf, "bsdf"]}
    }
}
